import { LiveElement, LiveNodeList } from 'live-node-list'

export const getScrollProgress = target => {
  if (target.scrollWidth === target.clientWidth) {
    return -1
  }

  return target.scrollLeft / (target.scrollWidth - target.clientWidth - 5)
}

export const goToSlide = (element, index, silent = false) => {
  if ('loop' in element.dataset) {
    if (index >= element.children.length) {
      index = 0
    } else if (index < 0) {
      index = element.children.length - 1
    }
  }

  const child = element.children[index]
  const currentIndex = [...element.children].indexOf(
    element.querySelector(':scope > [aria-current="true"]'),
  )
  const columnGap = window.getComputedStyle(element).columnGap
  const rowGap = window.getComputedStyle(element).rowGap
  const scrollOptions = {
    left: element.horizontal
      ? element.centered
        ? child.offsetLeft + element.clientWidth / 2 - child.clientWidth / 2
        : index > currentIndex
          ? child.offsetLeft
          : child.offsetLeft - columnGap
      : 0,
    top: element.horizontal
      ? 0
      : element.centered
        ? child.offsetTop + element.clientHeight / 2 - child.clientHeight / 2
        : index > currentIndex
          ? child.offsetTop
          : child.offsetTop - rowGap,
  }

  element?.scrollTo({
    behavior: silent ? 'instant' : 'smooth',
    ...scrollOptions,
  })
}

export const isHorizontal = element =>
  window.getComputedStyle(element).scrollSnapType.startsWith('x')

export const isCentered = element => {
  if (!element.firstElementChild) {
    return false
  }

  return (
    window.getComputedStyle(element.firstElementChild).scrollSnapAlign ===
    'center'
  )
}

export const setCurrentSlide = element => {
  if (!element) {
    return
  }

  const direction =
    element.scrollLeft > element.previousScroll ? 'right' : 'left'
  let gap = parseFloat(window.getComputedStyle(element).gap || 0)
  if (isNaN(gap)) {
    gap = 0
  }

  const horizontal = element.horizontal
  const centered = element.centered

  let padding =
    parseFloat(
      window.getComputedStyle(element)[
        horizontal ? 'paddingLeft' : 'paddingTop'
      ],
    ) || 0
  if (isNaN(padding)) {
    padding = 0
  }
  const containerEdge =
    element.getBoundingClientRect()[horizontal ? 'left' : 'top'] + padding

  let newElement
  const offset = 25
  for (let child of element.slides.items) {
    const edge =
      child.getBoundingClientRect()[horizontal ? 'left' : 'top'] - containerEdge

    if (centered) {
      const centeredEdge = edge - child.clientWidth / 2
      const threshold = horizontal
        ? element.clientWidth / 2
        : element.clientHeight / 2
      if (centeredEdge >= 0 && centeredEdge < threshold) {
        newElement = child
      }
    } else {
      const threshold =
        direction === 'right'
          ? (horizontal ? child.clientWidth : child.clientHeight) - gap - offset
          : 0 + gap + offset
      if (edge >= 0 - threshold) {
        newElement = child
      }
    }

    if (newElement) {
      break
    }
  }

  const current = element.querySelector('[aria-current="true"]')
  if (current) {
    current.removeAttribute('aria-current')
  }

  if (newElement) {
    newElement.setAttribute('aria-current', 'true')
  }

  element.previousScroll = element.scrollLeft

  if (element.dataset.autoplay) {
    autoplay(element, parseInt(element.dataset.autoplay, 10))
  }
}

export const autoplay = (element, interval) => {
  if (element.autoplayTimeout) {
    clearTimeout(element.autoplayTimeout)
  }

  element.autoplayTimeout = setTimeout(() => {
    const currentIndex = [...element.children].indexOf(
      element.querySelector(':scope > [aria-current="true"]'),
    )
    const nextIndex =
      currentIndex + 1 < element.children.length ? currentIndex + 1 : 0
    goToSlide(element, nextIndex)
  }, interval)
}

export const silentlySetCurrentSlide = element => {
  if (!element) {
    return
  }

  element.style.scrollBehavior = 'auto'
  setCurrentSlide(element)

  setTimeout(() => {
    delete element.style.scrollBehavior
  }, 10)
}

export const registerSlideshow = (control, config = {}) => {
  const id = control.getAttribute('aria-controls')
  const target = document.getElementById(id)
  control.slideshow = target

  if (!target.slides) {
    target.slides = new LiveNodeList(
      `:scope > [aria-hidden="false"], :scope > :not([aria-hidden])`,
      target,
      {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      },
    )
  }

  if (!target.currentSlide) {
    target.currentSlide = new LiveElement(
      `:scope > [aria-current="true"]`,
      target,
      {
        attributes: true,
        attributeFilter: ['aria-current'],
      },
    )
  }

  target.horizontal = isHorizontal(target)
  target.centered = isCentered(target)
  target.slides.on('update', silentlySetCurrentSlide.bind(this, target))
  target.addEventListener('scroll', silentlySetCurrentSlide.bind(this, target))
  window.addEventListener('resize', silentlySetCurrentSlide.bind(this, target))
  silentlySetCurrentSlide(target)

  if (config.onUpdateChildren) {
    target.slides.on('update', config.onUpdateChildren)
    config.onUpdateChildren()
  }

  if (config.onChange) {
    target.currentSlide.on('update', config.onChange)
    config.onChange()
  }

  if (config.onScroll) {
    target.slides.on('update', config.onScroll)
    target.addEventListener('scroll', config.onScroll)
    config.onScroll()
  }
}

export const unregisterSlideshow = (control, config = {}) => {
  const id = control.getAttribute('aria-controls')
  const target = document.getElementById(id)

  if (!target) {
    return
  }

  if (target.slides) {
    target.slides.off('update', silentlySetCurrentSlide.bind(this, target))
  }

  target.removeEventListener(
    'scroll',
    silentlySetCurrentSlide.bind(this, target),
  )

  if (config.onUpdateChildren && target.slides) {
    target.slides.off('update', config.onUpdateChildren)
  }

  if (config.onScroll) {
    target.removeEventListener('scroll', config.onScroll)
  }
}
